import { Link, useLocation } from 'react-router-dom';
import { useContext, useState, useCallback, useEffect } from 'react';
import { useLogout } from '../hooks/useLogout';
import { useAuthContext } from '../hooks/useAuthContext';
import { ProductsContext } from "../context/products.context";
import { FRAMEWORKS_URL } from '../utils/frameworksLink';
import Select from 'react-select';
import { User, LogOut, ChevronDown, Settings, ExternalLink } from 'lucide-react';
import {
  useFloating,
  offset,
  flip,
  shift,
  FloatingPortal,
} from '@floating-ui/react';

export default function Navbar() {
  const { logout, isPending } = useLogout();
  const { user, authIsReady } = useAuthContext();
  const {
    districtName,
    selectableDistricts,
    setDefaultDistrict,
    defaultDistrict
    // We no longer need navbarSelection / setNavbarSelection
  } = useContext(ProductsContext);

  // For checking which route is currently active
  const location = useLocation();

  // Sidebar awareness - listen for sidebar state changes
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const handleSidebarChange = (e) => {
      setSidebarOpen(e.detail.isOpen);
    };
    window.addEventListener('sidebar-state-changed', handleSidebarChange);
    return () => window.removeEventListener('sidebar-state-changed', handleSidebarChange);
  }, []);

  // Dropdown states
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isEquityDropdownOpen, setIsEquityDropdownOpen] = useState(false);
  const [isOverviewDropdownOpen, setIsOverviewDropdownOpen] = useState(false);

  // Floating UI setup for user dropdown
  const { x: userX, y: userY, strategy: userStrategy, refs: userRefs } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });

  // Floating UI setup for equity dropdown
  const { x: equityX, y: equityY, strategy: equityStrategy, refs: equityRefs } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  // Floating UI setup for overview dropdown
  const { x: overviewX, y: overviewY, strategy: overviewStrategy, refs: overviewRefs } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });
  
  // Close dropdowns when clicking outside
  const handleClickOutside = useCallback(
    (event) => {
      const userFloating = userRefs.floating.current;
      const userReference = userRefs.reference.current;
      const equityFloating = equityRefs.floating.current;
      const equityReference = equityRefs.reference.current;
      const overviewFloating = overviewRefs.floating.current;
      const overviewReference = overviewRefs.reference.current;

      // Check user dropdown
      if (
        userFloating &&
        !userFloating.contains(event.target) &&
        userReference &&
        !userReference.contains(event.target)
      ) {
        setIsUserDropdownOpen(false);
      }

      // Check equity dropdown
      if (
        equityFloating &&
        !equityFloating.contains(event.target) &&
        equityReference &&
        !equityReference.contains(event.target)
      ) {
        setIsEquityDropdownOpen(false);
      }

      // Check overview dropdown
      if (
        overviewFloating &&
        !overviewFloating.contains(event.target) &&
        overviewReference &&
        !overviewReference.contains(event.target)
      ) {
        setIsOverviewDropdownOpen(false);
      }
    },
    [userRefs, equityRefs, overviewRefs]
  );

  // Close on Escape key
  const handleEscape = useCallback((event) => {
    if (event.key === 'Escape') {
      setIsUserDropdownOpen(false);
      setIsEquityDropdownOpen(false);
      setIsOverviewDropdownOpen(false);
    }
  }, []);

  // Add event listeners
  useEffect(() => {
    if (isUserDropdownOpen || isEquityDropdownOpen || isOverviewDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isUserDropdownOpen, isEquityDropdownOpen, isOverviewDropdownOpen, handleClickOutside, handleEscape]);

  const customStyles = {
    option: (styles) => ({ ...styles, cursor: 'pointer' }),
    control: (styles) => ({ ...styles, cursor: 'pointer' }),
  };

  // Non-user links
  const navbarNonUserSelectables = [
    { label: "Login", link: "/login" },
    { label: "Signup", link: "/signup" },
  ];

  // Logged-in user links - reorganized for better grouping
  // shortLabel is for smaller breakpoints
  const navbarUserSelectables = [
    { label: "Advisor", shortLabel: "Advisor", link: "/" },
    { label: "CIP Report",     shortLabel: "CIP",   link: "/cip" },
    { label: "Enrollment",     shortLabel: "Enroll", link: "/enrollments" },
    // Regional view - only shown for ESD users (handled by filter below)
    { label: "Regional",       shortLabel: "Regional", link: "/regional", dropdownKey: "overview", esdOnly: true,
      submenu: [
        { label: "Regions", link: "/regional?view=compare" },
        { label: "Districts", link: "/regional?view=table" },
        { label: "Matrix", link: "/regional?view=matrix" },
        { label: "Charts", link: "/regional?view=stacked" }
      ]
    },
    { label: "Equity",         shortLabel: "Equity", link: "/equityallheatmap", dropdownKey: "equity",
      submenu: [
        { label: "By Group", link: "/equityallheatmap" },
        { label: "By Gender", link: "/equity" }
      ]
    },
    { label: "Indicators",     shortLabel: "Indicators", link: "/performanceindicators" },
    { label: "Labor Market",   shortLabel: "LMI",    link: "/lmi" },
    { label: "Summary",        shortLabel: "Summary", link: "/summary" },
    // Lives on frameworks.numetrics.org. Same Firebase project, but a
    // different origin means auth does not carry over — that app's dashboard
    // guard bounces signed-out arrivals to /login?from=/frameworks/.
    { label: "Frameworks",     shortLabel: "Frameworks", link: FRAMEWORKS_URL, external: true },
  ];
  
  // Get user initials for avatar
  const getUserInitials = (email) => {
    if (!email) return 'U';
    return email.charAt(0).toUpperCase();
  };

  // Only apply sidebar offset on the chat page (root path)
  const isChatPage = location.pathname === '/';
  const navbarLeftOffset = isChatPage && user ? (sidebarOpen ? 'left-64' : 'left-14') : 'left-0';

  return (
    <nav className={`print:hidden fixed top-0 ${navbarLeftOffset} right-0 z-[60] bg-white text-gray-600 h-16 transition-[left] duration-300 border-b border-gray-200 shadow-sm`}>
      {authIsReady && (
        <div className="container mx-auto px-4 flex items-center justify-between h-full">
          {/* Logo / Brand with District Selector */}
          <div className="flex items-center h-full space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-indigo-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M12 14l9-5-9-5-9 5 9 5z" />
              <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422
                   a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012
                   20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078
                   0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
              />
            </svg>

            {/* District Selector (if user has multiple districts) */}
            {user && selectableDistricts && selectableDistricts.length > 1 ? (
              <div className="min-w-[200px] text-slate-800">
                <Select
                  styles={customStyles}
                  value={defaultDistrict}
                  options={selectableDistricts}
                  onChange={(e) => setDefaultDistrict(e)}
                />
              </div>
            ) : (
              <span className="font-bold text-base lg:text-lg text-gray-800">
                {user ? districtName : ""}
              </span>
            )}

            <span className="font-bold text-base lg:text-lg text-gray-800">
              CTElink
            </span>
          </div>

          {/* Right-side Navigation */}
          {!user ? (
            <ul className="flex items-center h-full space-x-1">
              {navbarNonUserSelectables.map((item) => {
                const isActive = location.pathname === item.link;

                return (
                  <li
                    key={item.label}
                    className="relative flex flex-col items-center justify-center h-full"
                  >
                    <Link
                      to={item.link}
                      className={`block px-4 py-2 text-sm lg:text-base rounded-lg transition-colors
                                ${isActive
                                  ? 'text-indigo-600 font-semibold'
                                  : 'text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100'}`}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex items-center space-x-4 h-full overflow-hidden">
              {/* Main Nav Items */}
              <ul className="flex items-center h-full space-x-1">
                {navbarUserSelectables
                  .filter(item => !item.esdOnly || defaultDistrict?.value?.startsWith('ESD_'))
                  .map((item) => {
                  const isActive = item.external
                    ? false
                    : item.submenu
                    ? item.submenu.some(sub => {
                        // Handle links with query params
                        if (sub.link.includes('?')) {
                          return location.pathname + location.search === sub.link;
                        }
                        return location.pathname === sub.link;
                      }) || location.pathname === item.link.split('?')[0]
                    : location.pathname === item.link;

                  // Special handling for dropdown menus
                  if (item.submenu) {
                    const isOverview = item.dropdownKey === 'overview';
                    const isOpen = isOverview ? isOverviewDropdownOpen : isEquityDropdownOpen;
                    const setIsOpen = isOverview ? setIsOverviewDropdownOpen : setIsEquityDropdownOpen;
                    const refs = isOverview ? overviewRefs : equityRefs;

                    return (
                      <li
                        key={item.label}
                        className="relative flex items-center justify-center h-full"
                      >
                        <button
                          ref={refs.setReference}
                          onClick={() => setIsOpen(!isOpen)}
                          className={`flex items-center space-x-1 whitespace-nowrap px-2 py-2 text-xs lg:text-sm rounded-lg transition-colors
                                    ${isActive
                                      ? 'text-indigo-600 font-semibold'
                                      : 'text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100'}`}
                        >
                          <span className="hidden xl:inline">{item.label}</span>
                          <span className="inline xl:hidden">{item.shortLabel}</span>
                          <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </li>
                    );
                  }

                  // Links that leave the app entirely — new tab so the
                  // dashboard stays put behind them.
                  if (item.external) {
                    return (
                      <li
                        key={item.label}
                        className="relative flex items-center justify-center h-full"
                      >
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 whitespace-nowrap px-2 py-2 text-xs lg:text-sm rounded-lg transition-colors
                                     text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100"
                        >
                          <span className="hidden xl:inline">{item.label}</span>
                          <span className="inline xl:hidden">{item.shortLabel}</span>
                          {/* Decorative only, and the row is tight below xl —
                              the sr-only note below carries the meaning. */}
                          <ExternalLink size={12} className="hidden xl:inline text-gray-400" aria-hidden="true" />
                          <span className="sr-only">(opens in a new tab)</span>
                        </a>
                      </li>
                    );
                  }

                  // Regular menu items
                  return (
                    <li
                      key={item.label}
                      className="relative flex items-center justify-center h-full"
                    >
                      <Link
                        to={item.link}
                        className={`block whitespace-nowrap px-2 py-2 text-xs lg:text-sm rounded-lg transition-colors
                                  ${isActive
                                    ? 'text-indigo-600 font-semibold'
                                    : 'text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100'}`}
                      >
                        <span className="hidden xl:inline">{item.label}</span>
                        <span className="inline xl:hidden">{item.shortLabel}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Overview Dropdown Portal */}
              <FloatingPortal>
                {isOverviewDropdownOpen && (
                  <div
                    ref={overviewRefs.setFloating}
                    style={{
                      position: overviewStrategy,
                      top: overviewY ?? 0,
                      left: overviewX ?? 0,
                    }}
                    className="z-[9999] bg-white rounded-lg shadow-lg py-2"
                  >
                    {navbarUserSelectables.find(item => item.dropdownKey === 'overview')?.submenu.map(sub => (
                      <Link
                        key={sub.label}
                        to={sub.link}
                        onClick={() => setIsOverviewDropdownOpen(false)}
                        className={`block px-6 py-2.5 text-sm whitespace-nowrap transition-colors
                          ${location.pathname + location.search === sub.link
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </FloatingPortal>

              {/* Equity Dropdown Portal */}
              <FloatingPortal>
                {isEquityDropdownOpen && (
                  <div
                    ref={equityRefs.setFloating}
                    style={{
                      position: equityStrategy,
                      top: equityY ?? 0,
                      left: equityX ?? 0,
                    }}
                    className="z-[9999] bg-white rounded-lg shadow-lg py-2"
                  >
                    {navbarUserSelectables.find(item => item.dropdownKey === 'equity')?.submenu.map(sub => (
                      <Link
                        key={sub.label}
                        to={sub.link}
                        onClick={() => setIsEquityDropdownOpen(false)}
                        className={`block px-6 py-2.5 text-sm whitespace-nowrap transition-colors
                          ${location.pathname === sub.link
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </FloatingPortal>

              {/* User Avatar Dropdown with FloatingUI Portal */}
              <div className="relative">
                <button
                  ref={userRefs.setReference}
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center space-x-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {getUserInitials(user.email)}
                  </div>
                  <ChevronDown size={16} className={`text-gray-500 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Portal for dropdown */}
                <FloatingPortal>
                  {isUserDropdownOpen && (
                    <div
                      ref={userRefs.setFloating}
                      style={{
                        position: userStrategy,
                        top: userY ?? 0,
                        left: userX ?? 0,
                      }}
                      className="z-[9999] w-64 bg-white rounded-lg shadow-lg py-2 mt-2"
                    >
                      <div className="px-4 py-2 border-b">
                        <div className="flex items-center space-x-2">
                          <User size={16} className="text-gray-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.email}</p>
                            <p className="text-xs text-gray-500">Account</p>
                          </div>
                        </div>
                      </div>
                      <Link
                        to="/account"
                        onClick={() => setIsUserDropdownOpen(false)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 transition-colors"
                      >
                        <Settings size={16} />
                        <span>Account Settings</span>
                      </Link>
                      <button
                        onClick={() => {
                          setIsUserDropdownOpen(false);
                          logout();
                        }}
                        disabled={isPending}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 transition-colors"
                      >
                        <LogOut size={16} />
                        <span>{isPending ? 'Logging out...' : 'Logout'}</span>
                      </button>
                    </div>
                  )}
                </FloatingPortal>
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}